import { create } from 'zustand';
import {
  Employee,
  EmployeeCreateRequest,
  EmployeePatchRequest,
  ReinviteRequest,
  TasksScope,
} from '../types/employee.type';
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  patchEmployee,
  deleteEmployee,
  disconnectEmployeeGoogle,
  reinviteEmployee,
} from '../services/employee.service';

interface EmployeeState {
  employees: Employee[];
  isLoading: boolean;
  error: string | null;
  fetchEmployees: () => Promise<void>;
  addEmployee: (data: EmployeeCreateRequest) => Promise<Employee>;
  editEmployee: (id: string, data: EmployeeCreateRequest) => Promise<void>;
  patchEmployeeFields: (id: string, data: EmployeePatchRequest) => Promise<void>;
  removeEmployee: (id: string) => Promise<void>;
  sendAccessRequest: (employeeId: string, scope?: TasksScope) => Promise<{ message: string }>;
  disconnectGoogle: (employeeId: string) => Promise<void>;
}

export const useEmployeeStore = create<EmployeeState>((set) => ({
  employees: [],
  isLoading: false,
  error: null,

  fetchEmployees: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await getEmployees();
      set({ employees: res.data, isLoading: false });
    } catch (e: any) {
      set({ error: e.message || 'Failed to load employees', isLoading: false });
    }
  },

  addEmployee: async (data: EmployeeCreateRequest) => {
    const emp = await createEmployee(data);
    set(s => ({ employees: [emp, ...s.employees] }));
    return emp;
  },

  editEmployee: async (id: string, data: EmployeeCreateRequest) => {
    const updated = await updateEmployee(id, data);
    set(s => ({ employees: s.employees.map(e => e.id === id ? updated : e) }));
  },

  patchEmployeeFields: async (id: string, data: EmployeePatchRequest) => {
    const updated = await patchEmployee(id, data);
    set(s => ({ employees: s.employees.map(e => e.id === id ? updated : e) }));
  },

  removeEmployee: async (id: string) => {
    await deleteEmployee(id);
    set(s => ({ employees: s.employees.filter(e => e.id !== id) }));
  },

  sendAccessRequest: async (employeeId: string, scope: TasksScope = 'shared_spaces') => {
    const res = await reinviteEmployee(employeeId, { tasks_scope: scope });
    set(s => ({
      employees: s.employees.map(e =>
        e.id === employeeId
          ? { ...e, google_access_status: res.google_access_status, tasks_scope: scope }
          : e
      ),
    }));
    return { message: res.message };
  },

  disconnectGoogle: async (employeeId: string) => {
    const res = await disconnectEmployeeGoogle(employeeId);
    set(s => ({
      employees: s.employees.map(e =>
        e.id === employeeId
          ? { ...e, google_access_status: res.google_access_status, google_spaces: [], tasks_scope: null }
          : e
      ),
    }));
  },
}));
